import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, map } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import Loader from '../../../common/components/Loader'
import { Languages, Menu, MenuPayload } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deleteSettingsWebMenu,
  getSettingsLanguages,
  getSettingsWebMenu,
  updateSettingsWebMenu,
} from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Card, Button, Input, Checkbox } from '@/components/ui'
import SelectField from '@/components/select-field'
import { ChevronDown, ChevronUp } from 'lucide-react'

export interface IMenuValues {
  menues: MenuPayload[]
}

export const MenuPage: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [menues, setMenues] = useState<Menu[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [removeMenuState, setRemoveMenuState] = useState<number | null>(null)

  const [allLanguages, setAllLanguages] = useState<Languages[]>([])

  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        setAllLanguages(allLanguagesResponse.data)

        const resp = await getSettingsWebMenu()
        setMenues(resp.data)
        const temp = map(resp.data, (menu) => {
          const transTemp = map(allLanguagesResponse.data, lang => {
            return {
              locale: lang.locale,
              name: '',
            }
          })
          map(menu.translations, trans => {
            const index = findIndex(allLanguagesResponse.data, l => l.locale === trans.locale)
            if (index !== -1) {
              transTemp[index].locale = trans.locale
              transTemp[index].name = trans.name
            }
          })
          return ({
            id: menu.id,
            name: menu.name,
            url: menu.url,
            ordinal: menu.ordinal,
            deletable: menu.deletable,
            secondaryMenu: menu.secondaryMenu,
            parentPage: !!menu.parentPage ? {
              id: menu.parentPage?.id,
              name: menu.parentPage?.name
            } : null,
            translations: menu.translations, //TODO: Change to transTemp after languages is deployed
          })
        })
        setTimeout(() => {
          setValue('menues', temp)
        }, 200)

      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const deleteFunc = async () => {
    if (!removeMenuState) {
      return
    }
    try {
      await deleteSettingsWebMenu(menues[removeMenuState].id)
      const resp = await getSettingsWebMenu()

      setMenues(resp.data)
      setValue('menues', resp.data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const schema = yup.object().shape({
    menues: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          id: yup.string(),
          name: yup.string().required('Field required!'),
          url: yup.string().required('Field required!'),
          deletable: yup.boolean().required(),
          ordinal: yup.number().required('Field required!'),
          secondaryMenu: yup.boolean().required(),
          parentPage: yup
            .object()
            .shape({
              id: yup.string().optional(),
              name: yup.string().required(),
            })
            .optional()
            .nullable(),
          translations: yup
            .array()
            .min(0)
            .of(
              yup.object().shape({
                id: yup.string().optional().nullable(),
                locale: yup.string().required(),
                name: yup.string().required(),
              }),
            )
            .optional(),
        }),
      )
      .required(),
  })

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    trigger,
    control,
  } = useForm<IMenuValues>({
    mode: 'onChange',
    resolver: yupResolver<IMenuValues>(schema),
  })

  const { fields: menuFields, append: appendMenu } = useFieldArray({
    control,
    name: `menues`,
  })

  const changeOrder = async (direction: number, index: number) => {
    const data: MenuPayload[] = watch('menues')
    const temp = data[index + direction]
    data[index + direction] = data[index]
    data[index] = temp

    data[index].ordinal = index
    data[index + direction].ordinal = index + direction

    try {
      setLoading(true)
      const response = await updateSettingsWebMenu(data)
      if (response.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
        setMenues(response.data)
        setValue('menues', response.data)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async (index: number) => {
    const transTemp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
      }
    })
    map(menues[index].translations, (trans) => {
      const index = findIndex(allLanguages, (l) => l.locale === trans.locale)
      if (index !== -1) {
        transTemp[index].locale = trans.locale
        transTemp[index].name = trans.name
      }
    })
    setValue(`menues.${index}`, {
      id: menues[index].id,
      name: menues[index].name,
      url: menues[index].url,
      ordinal: menues[index].ordinal,
      deletable: menues[index].deletable,
      secondaryMenu: menues[index].secondaryMenu,
      parentPage: !!menues[index].parentPage ? menues[index].parentPage : null,
      translations: transTemp,
    })
  }

  const updateName = useCallback(async (index: number) => {
    const valid = await trigger(`menues.${index}`)

    if (!valid) {
      return
    }

    const payload = watch(`menues.${index}`)

    try {
      setLoading(true)
      const response = await updateSettingsWebMenu(
        [payload].map((one) => ({
          ...one,
          translations: one?.translations?.filter((one) => !!one.name),
        })),
      )
      if (response.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
        setMenues(response.data)
        setValue('menues', response.data)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <>
      {loading && <Loader />}
      <Header pageTitle="Menu" />
      <Card className="p-4 max-h-[calc(100vh-150px)] overflow-y-auto">
        <PageTitle title="Menu" />
        <form className="mt-4">
          {map(menuFields, (menu, index) => {
            const parent = watch(`menues.${index}.parentPage`)
            return (
              <Card key={menu.id} className="mb-4 p-4">
                <div className="flex flex-col items-start gap-2">
                  <div className="flex gap-2 items-center w-full">
                    <Button
                      variant="secondary"
                      className="border border-monochrome-120"
                      size="sm"
                      disabled={menu.ordinal === 0 || !hasEditPermission}
                      type="button"
                      onClick={() => changeOrder(-1, index)}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <span>{menu.ordinal + 1}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="border border-monochrome-120"
                      disabled={
                        menu.ordinal >= menuFields.length - 1 ||
                        !hasEditPermission
                      }
                      onClick={() => changeOrder(1, index)}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Input
                      isInvalid={!!errors?.menues?.[index]?.name?.message}
                      {...register(`menues.${index}.name`)}
                      type="text"
                      placeholder="Enter Name"
                      className="w-100"
                      disabled={!hasEditPermission}
                    />
                    <Button
                      variant="destructive"
                      type="button"
                      size="sm"
                      onClick={() => {
                        setDeleteConfirmationModal(true)
                        setRemoveMenuState(index)
                      }}
                      disabled={!hasEditPermission}
                    >
                      Delete
                    </Button>
                  </div>
                  <FormGroup label="URL Link">
                    <Input
                      isInvalid={!!errors?.menues?.[index]?.url?.message}
                      {...register(`menues.${index}.url`)}
                      type="text"
                      placeholder="Enter URL"
                      className="w-100"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup
                    label="Mark as Secondary Menu"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                  >
                    <Checkbox
                      isInvalid={
                        !!errors?.menues?.[index]?.secondaryMenu?.message
                      }
                      {...register(`menues.${index}.secondaryMenu`)}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          setValue(`menues.${index}.parentPage`, null, {
                            shouldValidate: true,
                          })
                        }
                      }}
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Select Parent Page">
                    <SelectField
                      options={map(
                        menues.filter((one) => one.name !== menu.name),
                        (menu) => ({
                          value: menu.id,
                          label: menu.name,
                        }),
                      )}
                      value={parent?.id || ''}
                      onValueChange={(value) => {
                        const parent = menues.find((one) => one.id === value)
                        if (parent) {
                          setValue(
                            `menues.${index}.parentPage`,
                            {
                              id: parent.id,
                              name: parent.name,
                            },
                            {
                              shouldValidate: true,
                            },
                          )
                        } else {
                          setValue(`menues.${index}.parentPage`, null, {
                            shouldValidate: true,
                          })
                        }
                      }}
                      disabled={!hasEditPermission}
                      error={errors?.menues?.[index]?.parentPage?.message}
                      placeholder="Select Parent"
                      outline
                    />
                  </FormGroup>
                  <div className="flex flex-col w-full gap-2">
                    {map(allLanguages, (language, i) => {
                      return (
                        <Card
                          className="flex flex-col p-3 gap-2 w-full border border-monochrome-120"
                          key={language.id}
                        >
                          <FormGroup
                            label="Language:"
                            direction="row"
                            wrapperClassName="mb-0"
                          >
                            <span className="text-sm font-semibold text-accent-1">
                              {language.name}
                            </span>
                          </FormGroup>
                          <FormGroup
                            label="Translation"
                            wrapperClassName="mb-0"
                          >
                            <Input
                              isInvalid={
                                !!errors?.menues?.[index]?.translations?.[i]
                                  ?.name?.message
                              }
                              {...register(
                                `menues.${index}.translations.${i}.name`,
                              )}
                              type="text"
                              disabled={!hasEditPermission}
                            />
                          </FormGroup>
                        </Card>
                      )
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
                  <Button
                    onClick={() => handleCancel(index)}
                    variant="secondary"
                    data-dismiss="modal"
                    disabled={!hasEditPermission}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => {
                      updateName(index)
                    }}
                    disabled={!hasEditPermission}
                  >
                    Save
                  </Button>
                </div>
              </Card>
            )
          })}
        </form>
        <Button
          onClick={() =>
            appendMenu({
              id: undefined,
              ordinal: menuFields.length,
              deletable: true,
              name: '',
              translations: map(allLanguages, (lang) => {
                return {
                  id: undefined,
                  locale: lang.locale,
                  name: '',
                }
              }),
              url: '',
              secondaryMenu: false,
              parentPage: null,
            })
          }
          type="button"
          className="w-full mt-2"
          variant="secondary"
          disabled={!hasEditPermission}
        >
          Add New Menu
        </Button>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Menu'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
